/* eslint complexity: ["error", {max: 7}] -- applies to entire file */
/* eslint max-statements: ["error", {max: 22}] -- this overrides for the entire file */
/* eslint max-lines-per-function: ["error", {max: 165}] -- very complex component */
import type { PropsWithChildren } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import isEmpty from "lodash/isEmpty";

import useAppMonitor from "@/services/AppMonitor";
import {
  useGetConsentsInfoQuery,
  useUpdateUserRecordingConsentMutation,
  useUpdateUserTermsAndPrivacyPolicyConsentMutation,
} from "@/store/api/user";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import {
  getIsLegalDocumentConsented,
  getPendingLegalDocuments,
  LegalDocuments,
} from "@/utils/legalDocuments.util";
import {
  hasRecordingConsentPermission,
  proxyPatientsNeedingRecordingConsent,
} from "@/utils/profile/permissions.util";

interface OnboardingContextProps {
  consents: {
    isTermsAndPrivacyAccepted: boolean;
    isTermsAndPrivacyAcceptedManually: boolean;
  };
  isRecordingConsentAlreadyExist: boolean;
  onPatientConsentChange: ({
    isChecked,
    patientId,
  }: PatientConsentChangeProps) => Promise<void>;
  onTermsAndPrivacyChange: (isChecked: boolean) => Promise<void>;
  patientConsentStates: Record<string, boolean>;
  patientsNeedingConsent: LinkedPatientProfile[] | undefined;
  shouldBypassConsentScreen: boolean;
  shouldShowConsentToRecording: boolean;
}

const OnboardingProvider = ({ children }: PropsWithChildren) => {
  const { captureException } = useAppMonitor();
  const [consents, setConsents] = useState<OnboardingContextProps["consents"]>({
    isTermsAndPrivacyAccepted: false,
    isTermsAndPrivacyAcceptedManually: false,
  });

  // State for tracking individual patient consent
  const [patientConsentStates, setPatientConsentStates] = useState<
    Record<string, boolean>
  >({});

  const {
    dateOfBirth,
    externalId: appUserExternalId,
    hasGivenRecordingConsent: hasAppUserGivenRecordingConsent,
    isPatient: isAppUserPatient,
  } = useAppUserSelector();

  const proxyPatients = useAppUserProxyPatientsSelector();
  const patientsNeedingConsent = useMemo(
    () => proxyPatientsNeedingRecordingConsent(proxyPatients),
    [proxyPatients],
  );

  const hasProxyPatientsGivenRecordingConsent = patientsNeedingConsent.every(
    (patient) => patient.hasGivenRecordingConsent ?? false,
  );
  const isRecordingConsentAlreadyExist =
    (isAppUserPatient && hasAppUserGivenRecordingConsent) ||
    (patientsNeedingConsent.length > 0 &&
      hasProxyPatientsGivenRecordingConsent);
  const shouldShowConsentToRecording = hasRecordingConsentPermission({
    appUserDateOfBirth: dateOfBirth,
    isAppUserPatient,
    proxyPatients,
  });

  const [updateUserRecordingConsent] = useUpdateUserRecordingConsentMutation();
  const [updateUserTermsAndPrivacyPolicyConsent] =
    useUpdateUserTermsAndPrivacyPolicyConsentMutation();
  const { data: consentsInfo } = useGetConsentsInfoQuery();

  const termsAndPrivacyConsentStatus =
    getIsLegalDocumentConsented(consentsInfo);
  const pendingLegalDocuments = getPendingLegalDocuments(consentsInfo);
  const shouldBypassConsentScreen =
    pendingLegalDocuments.length === 0 &&
    (!shouldShowConsentToRecording || isRecordingConsentAlreadyExist);

  useEffect(() => {
    const isTermsOfUseAccepted =
      termsAndPrivacyConsentStatus[LegalDocuments.TermsOfUse];
    const isPrivacyPolicyAccepted =
      termsAndPrivacyConsentStatus[LegalDocuments.PrivacyPolicy];

    setConsents((prev) => ({
      ...prev,
      isTermsAndPrivacyAccepted:
        isTermsOfUseAccepted && isPrivacyPolicyAccepted,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentsInfo]);

  const onTermsAndPrivacyChange = useCallback(
    async (isChecked: boolean) => {
      setConsents((prev) => ({
        ...prev,
        isTermsAndPrivacyAcceptedManually: true,
      }));
      try {
        await updateUserTermsAndPrivacyPolicyConsent(pendingLegalDocuments);
        setConsents((prev) => ({
          ...prev,
          isTermsAndPrivacyAccepted: isChecked,
        }));
      } catch (error) {
        captureException(error);
      }
    },
    [
      captureException,
      pendingLegalDocuments,
      updateUserTermsAndPrivacyPolicyConsent,
    ],
  );

  const onPatientConsentChange = useCallback(
    async ({
      isChecked,
      patientId,
    }: {
      isChecked: boolean;
      patientId: string;
    }) => {
      const patientExternalId = isAppUserPatient
        ? appUserExternalId
        : patientId;
      // Update local state
      setPatientConsentStates((prev) => ({
        ...prev,
        [patientExternalId]: isChecked,
      }));
      // If patientExternalId is nil, we cannot update the backend
      if (isEmpty(patientExternalId)) {
        captureException(
          new Error(
            "Error: Cannot update recording consent for user without externalId",
          ),
        );
        return;
      }

      // Update backend
      try {
        await updateUserRecordingConsent({
          hasGivenRecordingConsent: isChecked,
          patientExternalId,
        });
      } catch (error) {
        captureException(error);
      }
    },
    [
      appUserExternalId,
      captureException,
      isAppUserPatient,
      updateUserRecordingConsent,
    ],
  );

  // this memoization may seem unnecessary, but this prevents needless rerenders
  // (potentially on every single render) due to the literal object {} being used directly
  const value = useMemo(
    () => ({
      consents,
      isRecordingConsentAlreadyExist,
      onPatientConsentChange,
      onTermsAndPrivacyChange,
      patientConsentStates,
      patientsNeedingConsent,
      shouldBypassConsentScreen,
      shouldShowConsentToRecording,
    }),
    [
      consents,
      isRecordingConsentAlreadyExist,
      onPatientConsentChange,
      onTermsAndPrivacyChange,
      patientConsentStates,
      patientsNeedingConsent,
      shouldBypassConsentScreen,
      shouldShowConsentToRecording,
    ],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

const OnboardingContext = createContext<OnboardingContextProps | undefined>(
  undefined,
);

const useOnboarding = (): OnboardingContextProps => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
};

export type { OnboardingContextProps };
export { useOnboarding };

export default OnboardingProvider;
